import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import clsx from 'clsx';
import i18n from 'i18next';

import { Loader, Option, Select } from '@adeccoux/tag-ds';
import AuthLayout from '@components/shared/AuthLayout/AuthLayout';
import { initApp } from '@helpers/app.helper';
import { setMomentLocale } from '@helpers/date-locales.helper';
import {
  getLanguage,
  getTenantByDomain,
  getTenantByDomainInstance,
  getTenantDefaultLanguage,
  getTenantInfo,
  storeTenant,
  tenantMap,
} from '@helpers/tenant.helper';
import { store } from '@store/index';
import { authAppSlice } from '@store/slices/authApp.slice';

import styles from './TenantSwitcher.module.scss';

export const storeNewTenant = async (tenantId: number) => {
  store.dispatch(authAppSlice.actions.clear());
  localStorage.removeItem('clientOrganizationId');
  localStorage.removeItem('selectedConfigurationId');
  localStorage.removeItem('redux_admin_auth_state');
  storeTenant(tenantId);
  if (i18n?.services?.languageUtils) {
    const lang = getLanguage(tenantId);
    await i18n.changeLanguage(lang);
  }
};

const TenantSwitcher: React.FC = () => {
  const [renderView, setRenderView] = useState(false);
  const [tenant, setTenant] = useState(getTenantInfo());
  const [lang, setLang] = useState('en');

  const history = useHistory();
  const { t } = useTranslation(['recruiter']);
  const locationStateData = history.location?.state?.data;

  const changeLanguageHandler = useCallback(
    (lang: string) => {
      i18n.changeLanguage(lang);
      setMomentLocale(tenant.tenantId, lang);
    },
    [tenant],
  );

  useEffect(() => {
    changeLanguageHandler(lang);
    const recruiterDefaultLanguage = getTenantDefaultLanguage(tenant.tenantId);
    if (window.location.pathname.includes('/oo/recruiter') && lang !== recruiterDefaultLanguage) {
      setLang(recruiterDefaultLanguage);
      changeLanguageHandler(lang);
    }
  }, [tenant, lang, changeLanguageHandler]);

  const reInit = async (tenantId: number, pathname: string = '/oo/recruiter'): Promise<void> => {
    await storeNewTenant(tenantId);
    await initApp();
    // pathname can include hashes, so we need to use location.href instead of location.pathname in order to keep properly the hashes.
    window.location.href = pathname;
  };

  useEffect(() => {
    const asyncCall = async (): Promise<void> => {
      const tenantByDomain = getTenantByDomain();
      if (tenantByDomain) {
        // no need to re-init, if tenantByDomain exists, initial appInit is valid, so just soft-redirect
        history.push('/oo/recruiter');
      } else {
        setRenderView(true);
      }
    };
    asyncCall();
  }, [history]);

  const handleSelectTenant = (newTenant: number) => {
    const newTenantId = newTenant ? parseInt(newTenant.toString(), 10) : tenant.tenantId;
    setTenant({ tenantId: newTenantId, tenantAlias: tenantMap['tid' + newTenantId].alias });
  };

  const handleContinue = async (): Promise<void> => {
    const CandidatePathname = locationStateData?.hash ? `/app/aadlogin/token${locationStateData?.hash}` : '/oo/';
    const RecruiterPathname = '/oo/recruiter';
    const pathname = window.location.pathname.includes(RecruiterPathname) ? RecruiterPathname : CandidatePathname;
    window.history.replaceState({}, '');
    if (tenant.tenantId !== getTenantInfo().tenantId) {
      await reInit(tenant.tenantId, pathname);
    } else {
      history.push(pathname);
    }
  };

  if (!renderView) return <Loader />;
  const tenantListState = locationStateData?.tenantIds;
  const tenantByDomainInstance = getTenantByDomainInstance();
  const tenantOptionsKeys = tenantListState?.length
    ? Object.keys(tenantByDomainInstance).filter((key) => tenantListState.includes(key))
    : Object.keys(tenantByDomainInstance);

  const tenantOptions = tenantOptionsKeys.reduce((acc: { [key: string]: any }, key: string) => {
    acc[key] = tenantByDomainInstance[key];
    return acc;
  }, {});

  const options = Object.keys(tenantOptions).map((key) => ({
    [tenantMap[key].name || tenantMap[key].countryCode]: parseInt(key.replace('tid', ''), 10),
  }));

  return (
    <AuthLayout>
      <h2 className={styles.heading}>{t('GENERAL.TENANT_SELECTION.changeCountrySpecificTenantLabel')}</h2>
      <Select
        className={styles.select}
        name="select"
        onChange={(value) => handleSelectTenant(value)}
        placeholder="Select an Option"
        value={tenant.tenantId}
        large
        label={t('GENERAL.TENANT_SELECTION.selectTenant')}
      >
        {options.map((option, i) => {
          const key = Object.keys(option)[0];

          return <Option key={i} value={option[key]} label={key} style={{ width: '100%' }} />;
        })}
      </Select>
      <button className={clsx('button_large', styles.button)} onClick={handleContinue}>
        CONTINUE
      </button>
    </AuthLayout>
  );
};

export default TenantSwitcher;
